import React from 'react'
import { Input, message } from 'antd'
import i18n from 'i18n'
import classNames from 'classnames'
import { isGlobal } from 'conf'
import WorkspaceService from 'api/Workspace'
import { getPlanLicense } from 'utils/plan'
import { confirmBase, errorBase } from 'utils/ui'
import styles from './style.module.scss'

const { TextArea } = Input

// 서비스 신청(유료전환) 확인 팝업 — 앱 표준 팝업(confirmBase, om-popup-1) 사용.
// onOk 가 reject 되면 antd confirm 이 닫히지 않으므로(실패 시 팝업 유지),
// 성공 시에만 닫히고 요청 중에는 확인 버튼이 자동 loading 처리된다.
// 워크스페이스 소유자 이름은 열림과 동시에 resource-quota API 로 조회해 modal.update 로 채운다.
export function openUpgradeRequestConfirm({ workspaceName } = {}) {
  // TextArea 는 uncontrolled — 입력값은 클로저에 보관 (modal.update 리렌더에도 DOM 값 유지)
  const formState = { userMessage: '' }

  // 요금제 안내 (JP 요금제 4종 개편, 2026-08-26) — 신청 전에 알아야 할 최소 정보만 준다.
  // ① 체험판이 지금 프로페셔널 수준이라는 사실(모르면 베이직 전환 후 "왜 인증 코드가
  // 안 되냐" 문의가 난다) ② 등급이 실제로 바꾸는 것 2가지. 계약 수량별 용량 산식 등
  // 나머지는 담당자 상담 몫이라 넣지 않는다(팝업 520px, 전환율 우선).
  // JP 전용 — 등급 개념 자체가 일본향에만 있어 미국향에서는 렌더하지 않는다.
  // MB 단위 표기 — utils 의 formatBytes 를 쓰지 않는다: 그 모듈이 query-string(ESM) 까지
  // 끌어와 jest 가 이 파일을 파싱하지 못한다(ResourceQuotaCard 도 같은 이유로 자체 포맷).
  const formatMb = (bytes) => `${Math.round(bytes / 1024 ** 2)} MB`

  // 플랜 미적용(플래그 OFF·명부 없음) 시 표에 쓰는 정책 기본값 — 백엔드
  // JP_PRO_MAX_FILE_SIZE 기본값 30MB 와 같아야 한다(settings/base.py).
  const PRO_FILE_SIZE_FALLBACK = '30 MB'

  const buildPlanInfo = () => {
    if (!isGlobal()) return null
    // 체험판은 PRO 동급이라 현재 상한이 곧 프로페셔널 상한 — license 값을 쓴다(특약·env
    // 조정 반영). 단 **플랜이 적용된 상태에서만** 신뢰할 수 있다:
    // JP_ATTACHMENT_PLAN_ENFORCE 가 OFF 면 max_file_size_bytes 가 legacy(10MB)로 떨어져
    // 표가 "베이직 10 MB / 프로페셔널 10 MB" 가 된다 — 수치 차이가 통째로 사라지는데
    // 그 플래그의 운영 기본값이 OFF 이고 배포 순서도 "OFF 배포 → 백필 → ON" 이라
    // 롤아웃 구간 전체가 이 상태다(2026-08-26 검토). 플랜 미적용이면 정책 기본값을 쓴다.
    // 활성 판정: storage_quota_bytes / personal_storage_quota_bytes 는 플랜 적용 시에만
    // 내려온다(workspace_license_service) — 둘 중 하나라도 있으면 활성.
    const license = getPlanLicense()
    const isPlanActive =
      license?.storage_quota_bytes != null || license?.personal_storage_quota_bytes != null
    const proLimitBytes = license?.max_file_size_bytes
    const proLimit =
      isPlanActive && proLimitBytes > 0 ? formatMb(proLimitBytes) : PRO_FILE_SIZE_FALLBACK

    // 등급이 실제로 바꾸는 3가지만 ✓/✗ 로 비교한다. 과금 방식(레코드/사용자 기준)은
    // 신청 시점에 사용자가 정할 사항이 아니라 담당자가 규모를 듣고 안내하는 부분이라 뺐다.
    const rows = [
      { key: 'totp', label: i18n.t('topBar.upgradeModal.planFeatureTotp'), basic: false },
      { key: 'sso', label: i18n.t('topBar.upgradeModal.planFeatureSso'), basic: false },
      {
        key: 'fileSize',
        label: i18n.t('topBar.upgradeModal.planFeatureFileSize'),
        basicText: '10 MB',
        proText: proLimit,
      },
    ]

    return (
      <div className={styles.planInfo}>
        <span className={styles.planTitle}>{i18n.t('topBar.upgradeModal.planTitle')}</span>
        <p className={styles.planNote}>{i18n.t('topBar.upgradeModal.planTrialNote')}</p>
        <div className={styles.planTable}>
          <div className={styles.planHeadRow}>
            <span className={styles.planFeature} />
            <span className={styles.planCol}>{i18n.t('appConfig.settings.tierBasic')}</span>
            <span className={styles.planCol}>{i18n.t('appConfig.settings.tierProfessional')}</span>
          </div>
          {rows.map((row) => (
            <div key={row.key} className={styles.planBodyRow}>
              <span className={styles.planFeature}>{row.label}</span>
              <span className={classNames(styles.planCol, { [styles.planNo]: !row.basicText })}>
                {row.basicText || '✕'}
              </span>
              <span className={classNames(styles.planCol, styles.planYes)}>
                {row.proText || '✓'}
              </span>
            </div>
          ))}
        </div>
        {/* 계약 방식(축)은 등급표로 표현할 수 없다 — 같은 프로페셔널이라도 레코드 기준은
            회사 합산, 사용 인원 기준은 1인당으로 용량 계산이 갈리기 때문. 신청 시점에
            고를 필요는 없으므로 "이런 차이가 있다"만 알리고 선택은 상담으로 넘긴다.
            수치는 정책 상수(회사 10GB / 레코드당 300MB / 1인당 2GB)라 문구에 둔다 —
            license 는 이미 정해진 축의 값만 주므로 두 방식을 나란히 보여줄 수 없다. */}
        <div className={styles.planStorage}>
          <span className={styles.planStorageTitle}>
            {i18n.t('topBar.upgradeModal.planStorageTitle')}
          </span>
          <ul className={styles.planStorageList}>
            <li>{i18n.t('topBar.upgradeModal.planStorageBasic')}</li>
            <li>{i18n.t('topBar.upgradeModal.planStorageProResource')}</li>
            <li>{i18n.t('topBar.upgradeModal.planStorageProUser')}</li>
          </ul>
        </div>
      </div>
    )
  }

  const buildContent = (ownerEmail) => (
    <div className={styles.upgradeModalBody}>
      <p className={styles.description}>{i18n.t('topBar.upgradeModal.description')}</p>
      <div className={styles.infoRow}>
        <span className={styles.label}>{i18n.t('topBar.upgradeModal.workspaceLabel')}</span>
        <span className={styles.value}>{workspaceName}</span>
      </div>
      <div className={styles.infoRow}>
        <span className={styles.label}>{i18n.t('topBar.upgradeModal.ownerLabel')}</span>
        <span className={styles.value}>{ownerEmail || '-'}</span>
      </div>
      {buildPlanInfo()}
      <div className={styles.messageField}>
        <span className={styles.label}>{i18n.t('topBar.upgradeModal.messageLabel')}</span>
        <TextArea
          rows={4}
          maxLength={1000}
          placeholder={i18n.t('topBar.upgradeModal.messagePlaceholder')}
          // modal.update 리렌더로 DOM 이 교체돼도 입력값이 유지되도록 defaultValue 로 복원
          defaultValue={formState.userMessage}
          onChange={(e) => {
            formState.userMessage = e.target.value
          }}
        />
      </div>
    </div>
  )

  // 팝업이 닫힌 뒤 늦게 도착한 소유자 조회 응답이 modal.update 를 호출하지 않도록 가드
  let isClosed = false

  const modal = confirmBase({
    title: i18n.t('topBar.upgradeModal.title'),
    // 560 (기존 520) — 등급 비교표 칼럼을 112px 로 넓히면서 기능 라벨 칼럼이 좁아졌다.
    // en "Sign in with company account (SSO)" 가 207px 라 520 에서는 두 줄로 접힌다.
    width: 560,
    content: buildContent(null),
    okText: i18n.t('topBar.upgradeModal.submit'),
    cancelText: i18n.t('topBar.upgradeModal.cancel'),
    okButtonProps: {
      className: 'type8 h38',
      style: { minWidth: '100px' },
      disabled: !workspaceName,
    },
    maskClosable: false,
    onCancel: () => {
      isClosed = true
    },
    onOk: async () => {
      try {
        await WorkspaceService.requestUpgrade(workspaceName || '', formState.userMessage)
        message.success(i18n.t('topBar.upgradeModal.successMessage'))
        isClosed = true
      } catch (e) {
        // 429(쿨타임) 안내는 공통 통로(services/jwt customFetch)가 이미 같은 문구로 띄운다 —
        // 여기서 또 띄우면 팝업이 두 개가 되므로 건너뛴다. throw 는 유지해야 팝업이 닫히지 않아
        // 사용자가 입력한 메시지를 잃지 않는다.
        if (e?.code !== 429) {
          errorBase({
            title: i18n.t('topBar.upgradeModal.errorTitle'),
            content: i18n.t('topBar.upgradeModal.errorMessage'),
          })
        }
        throw e
      }
    },
  })

  // 워크스페이스 소유자 이메일 지연 로드 — 실패해도 팝업 동작엔 영향 없음('-' 유지)
  WorkspaceService.getResourceQuota()
    .then((res) => {
      const ownerEmail = res?.data?.owner_email
      if (ownerEmail && !isClosed) modal.update({ content: buildContent(ownerEmail) })
    })
    .catch(() => {})

  return modal
}

export default openUpgradeRequestConfirm
